// Global Instructions Rule Applied!
//
// Late-closer Equalization section — the ONE equalization surface, shared
// verbatim by the fund full-form Capital Calls tab and the consolidated
// fund's Capital Calls tab (owner ruling 2026-08-08: the group form uses
// the same UI as a normal fund wherever it makes sense).
//
// Self-contained: fetches view__fund_equalization_preview for its fund,
// owns the Top-up/Reallocate call-level choice, the Excel export and the
// Create Equalization Call wizard (drafts a plain capital call whose lines
// carry per-line catch-up modes — editable while Draft). On a consolidated
// PRIMARY the backend weighs every LP by their gross subscription, so this
// same component IS the group-level equalization view when pointed at the
// primary fund.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import dayjs from 'dayjs'
import { Button, Modal, Segmented, Space, Table, Tag, Typography } from 'antd'
import DatePicker from '../../../core/components/DatePicker.tsx'
import { DownloadOutlined, PlusOutlined } from '@ant-design/icons'
import { toast } from 'react-toastify'
import { showErrorToast } from '../../../core/utils/errorToast.tsx'
import { FormSection } from '../../../core/components/FormSection.tsx'
import { getAccessToken } from '../../../core/auth/authService'
import { store } from '../../../state_manager/store.ts'
import { useTabManagerContext } from '../../../core/app-layout/lib/TabManagerContext.tsx'
import { TABLE_CAPITAL_CALLS } from '../../../state_manager/globals.ts'
import {
  createEqualizationCall,
  fetchFundEqualizationPreview
} from '../services/capitalCallsService.ts'
import type {
  EqualizationPreview,
  EqualizationPreviewRow
} from '../../../state_manager/data_models/capital_call_equalization.ts'
import { exportJsonToExcel } from '../../../core/services/utilitiesService.ts'

const { Text } = Typography

const disp = (v: unknown): string => (v != null && v !== '' ? String(v) : '-')

const DATE_FORMAT = 'MM/DD/YYYY'
const ISO_DATE = 'YYYY-MM-DD'

// a late closer the preview says still owes something under the chosen mode
const owesCatchup = (r: EqualizationPreviewRow, rateTopup: boolean): boolean =>
  Boolean(r.prog__is_late_closer) &&
  ((r.prog__fee_catchup ?? 0) >= 0.005 ||
    (r.prog__fixed_realloc_delta ?? 0) >= 0.005 ||
    ((rateTopup ? r.prog__rate_topup_delta : r.prog__rate_realloc_delta) ?? 0) >= 0.005)

interface EqualizationCallDateFieldProps {
  initialDate: string
  rows: EqualizationPreviewRow[]
  rateTopup: boolean
  onChange: (isoDate: string) => void
}

// Call Date inside the Create Equalization Call confirm. The confirm content
// is rendered once, so the field owns its state and reports up via onChange.
// The call bills only LPs closed on or before this date, so the note counts
// which of the preview's outstanding late closers the date includes.
function EqualizationCallDateField({
  initialDate,
  rows,
  rateTopup,
  onChange
}: EqualizationCallDateFieldProps): React.ReactElement {
  const [callDate, setCallDate] = useState(initialDate)
  const owing = rows.filter((r) => owesCatchup(r, rateTopup))
  const included = owing.filter(
    (r) => !r.fund_commitments__date_closed || r.fund_commitments__date_closed <= callDate
  ).length
  const excluded = owing.length - included
  return (
    <div className='mb-3'>
      <div className='mb-1 text-gray-500 text-sm'>Call Date</div>
      <DatePicker
        className='w-48'
        value={dayjs(callDate)}
        format={DATE_FORMAT}
        allowClear={false}
        onChange={(d) => {
          if (!d) return
          const iso = d.format(ISO_DATE)
          setCallDate(iso)
          onChange(iso)
        }}
      />
      <Text type='secondary' className='block mt-1'>
        {included === 0
          ? 'No outstanding late closer had closed by this date.'
          : `Bills ${included} late closer${included === 1 ? '' : 's'} closed on or before this date`}
        {included > 0 && excluded > 0
          ? `; ${excluded} who closed later ${excluded === 1 ? 'is' : 'are'} left for a later call.`
          : included > 0
            ? '.'
            : ''}{' '}
        Only calls dated on or before it are caught up.
      </Text>
    </div>
  )
}

// equalization deltas: zero = quiet dash, negative = green credit tag
const renderDelta = (raw: number | null | undefined, fmt: string | null | undefined) => {
  const delta = raw ?? 0
  if (Math.abs(delta) < 0.005) return <Text type='secondary'>—</Text>
  if (delta < 0)
    return (
      <span>
        <Text type='success'>{disp(fmt)}</Text>
        <Tag color='purple' className='ml-2'>
          credit
        </Tag>
      </span>
    )
  return disp(fmt)
}

interface EqualizationSectionProps {
  fundId: string
  /** file-name stem for the Excel export (defaults to the fund's name) */
  exportName?: string | null
  /** parent refresh after the wizard drafts a call (e.g. reload a calls grid) */
  onDraftCreated?: () => void
}

export function EqualizationSection({
  fundId,
  exportName,
  onDraftCreated
}: EqualizationSectionProps): React.ReactElement {
  const { openFormInTab } = useTabManagerContext()
  // static Modal.confirm does not render under React 19 - hook form required
  const [modalApi, modalContextHolder] = Modal.useModal()

  const [equalization, setEqualization] = useState<EqualizationPreview | null>(null)
  const [loading, setLoading] = useState(false)
  const [creatingEqCall, setCreatingEqCall] = useState(false)
  // rate-specified source groups follow this call-level choice; fixed
  // groups always draft as reallocate (a fixed cost does not grow when an
  // LP joins). Modes stay editable per line on the drafted call.
  const [rateTopup, setRateTopup] = useState(true)
  // call date chosen in the confirm dialog (reset to today on every open)
  const eqCallDateRef = useRef<string>(dayjs().format(ISO_DATE))

  const refetch = useCallback(async () => {
    if (!fundId) return
    setLoading(true)
    try {
      const token = getAccessToken(store.getState().auth)
      const res = await fetchFundEqualizationPreview(fundId, token)
      if (res.error) showErrorToast(res.error)
      setEqualization(res.data)
    } finally {
      setLoading(false)
    }
  }, [fundId])

  useEffect(() => {
    void refetch()
  }, [refetch])

  const eqRows = useMemo(() => equalization?.data ?? [], [equalization])
  const eqGroups = useMemo(() => equalization?.groups ?? [], [equalization])
  const eqPolicy = equalization?.late_closer_policy ?? null
  const eqTotals = equalization?.totals ?? null
  const rateChargeTotal = rateTopup
    ? eqTotals?.rate_topup_charge_total ?? 0
    : eqTotals?.rate_realloc_charge_total ?? 0
  const eqOutstanding =
    Math.abs(eqTotals?.fee_catchup_total ?? 0) >= 0.005 ||
    Math.abs(eqTotals?.fixed_realloc_charge_total ?? 0) >= 0.005 ||
    Math.abs(rateChargeTotal) >= 0.005

  const handleCreateEqualizationCall = useCallback(() => {
    const groupLines = eqGroups.map((g) => {
      const isRate = g.spec === 'rate'
      const mode = isRate ? (rateTopup ? 'top-up' : 'reallocate') : 'reallocate'
      return `${g.item_type_label ?? 'Contribution'} (${g.spec ?? 'all'}) → ${mode}`
    })
    const today = dayjs().format(ISO_DATE)
    eqCallDateRef.current = today
    modalApi.confirm({
      title: 'Create equalization call?',
      content: (
        <div>
          <EqualizationCallDateField
            initialDate={today}
            rows={eqRows}
            rateTopup={rateTopup}
            onChange={(iso) => {
              eqCallDateRef.current = iso
            }}
          />
          <p>
            Drafts a dedicated capital call with the outstanding fee catch-up plus one
            contribution catch-up line per source group:
          </p>
          <ul className='pl-5 mb-2'>
            {groupLines.map((l) => (
              <li key={l}>{l}</li>
            ))}
          </ul>
          <p className='mb-0'>
            {rateTopup
              ? 'Top-up: late closers contribute at the original call rates — no credits to early closers.'
              : 'Reallocate: contributions re-pool as-if-from-day-one — early closers receive credits.'}{' '}
            Every line&apos;s mode stays editable on the Draft.
          </p>
        </div>
      ),
      okText: 'Create draft',
      onOk: async () => {
        setCreatingEqCall(true)
        try {
          const token = getAccessToken(store.getState().auth)
          const res = await createEqualizationCall(
            String(fundId),
            rateTopup,
            eqCallDateRef.current,
            token
          )
          if (res.error || !res.data) {
            showErrorToast(res.error || 'Failed to create equalization call')
            return
          }
          toast.success('Equalization call drafted — review and issue')
          openFormInTab(
            TABLE_CAPITAL_CALLS,
            res.data.call.id,
            res.data.call.name || 'Equalization Call'
          )
          await refetch()
          onDraftCreated?.()
        } finally {
          setCreatingEqCall(false)
        }
      }
    })
  }, [fundId, modalApi, openFormInTab, refetch, onDraftCreated, rateTopup, eqGroups, eqRows])

  const exportFundName = useCallback(() => {
    if (exportName) return exportName
    const funds = (store.getState() as any).globalConfigs?.funds ?? []
    return funds.find((f: any) => f.id === fundId)?.name ?? 'fund'
  }, [exportName, fundId])

  const handleExportEqualization = useCallback(async () => {
    const token = getAccessToken(store.getState().auth)
    const res = await exportJsonToExcel(
      {
        data: eqRows.map((r) => ({
          Investor: r.investors__name ?? '',
          'Late Closer': r.prog__is_late_closer ? 'Yes' : '',
          Commitment: r.fund_commitments__amount ?? 0,
          'Date Closed': r.fund_commitments__date_closed ?? '',
          'Fee Catch-up': r.prog__fee_catchup ?? 0,
          'Contribution Δ (fixed, reallocate)': r.prog__fixed_realloc_delta ?? 0,
          'Contribution Δ (rate, top-up)': r.prog__rate_topup_delta ?? 0,
          'Contribution Δ (rate, reallocate)': r.prog__rate_realloc_delta ?? 0
        })),
        columns: [
          'Investor',
          'Late Closer',
          'Commitment',
          'Date Closed',
          'Fee Catch-up',
          'Contribution Δ (fixed, reallocate)',
          'Contribution Δ (rate, top-up)',
          'Contribution Δ (rate, reallocate)'
        ],
        filename: `equalization_${exportFundName()}`,
        dateColumns: ['Date Closed']
      },
      token
    )
    if (!res.success) showErrorToast(res.error || 'Export failed')
  }, [eqRows, exportFundName])

  const equalizationColumns = useMemo(
    () => [
      {
        title: 'Investor',
        dataIndex: 'investors__name',
        render: (v: string | null, row: EqualizationPreviewRow) => (
          <span>
            {disp(v)}
            {row.prog__is_late_closer ? (
              <Tag color='blue' className='ml-2'>
                late closer
              </Tag>
            ) : null}
          </span>
        )
      },
      {
        title: 'Commitment',
        dataIndex: 'fund_commitments__amount_fmt',
        width: 130,
        align: 'right' as const,
        render: disp
      },
      { title: 'Date Closed', dataIndex: 'fund_commitments__date_closed_fmt', width: 110, render: disp },
      {
        title: 'Fee Catch-up',
        dataIndex: 'prog__fee_catchup_fmt',
        width: 120,
        align: 'right' as const,
        render: (v: string | null, row: EqualizationPreviewRow) =>
          Math.abs(row.prog__fee_catchup ?? 0) < 0.005 ? <Text type='secondary'>—</Text> : disp(v)
      },
      {
        title: 'Contribution Δ (fixed)',
        dataIndex: 'prog__fixed_realloc_delta_fmt',
        width: 140,
        align: 'right' as const,
        render: (v: string | null, row: EqualizationPreviewRow) =>
          renderDelta(row.prog__fixed_realloc_delta, v)
      },
      {
        title: 'Contribution Δ (rate)',
        key: 'rate_delta',
        width: 150,
        align: 'right' as const,
        render: (_: unknown, row: EqualizationPreviewRow) =>
          rateTopup
            ? renderDelta(row.prog__rate_topup_delta, (row as any).prog__rate_topup_delta_fmt)
            : renderDelta(row.prog__rate_realloc_delta, (row as any).prog__rate_realloc_delta_fmt)
      }
    ],
    [rateTopup]
  )

  return (
    <FormSection
      title='Equalization'
      titleColor='orange'
      loading={loading}
      headerActions={
        <Space>
          <Segmented
            size='small'
            value={rateTopup ? 'topup' : 'realloc'}
            options={[
              { value: 'topup', label: 'Top-up' },
              { value: 'realloc', label: 'Reallocate' }
            ]}
            onChange={(v) => setRateTopup(v === 'topup')}
          />
          <Button
            size='small'
            icon={<DownloadOutlined />}
            disabled={eqRows.length === 0}
            onClick={handleExportEqualization}
          >
            Export
          </Button>
          <Button
            size='small'
            type='primary'
            icon={<PlusOutlined />}
            loading={creatingEqCall}
            disabled={eqPolicy !== 'Full catch-up' || !eqOutstanding}
            onClick={handleCreateEqualizationCall}
          >
            Create Equalization Call
          </Button>
        </Space>
      }
    >
      {modalContextHolder}
      {eqPolicy !== 'Full catch-up' ? (
        <Text type='secondary'>
          Late-closer policy: {eqPolicy ?? 'not set'} — set it to &quot;Full catch-up&quot; on the
          Terms tab to bill late closers as if they had closed at the fee schedule start and to
          equalize contributions.
        </Text>
      ) : eqRows.length === 0 ? (
        <Text type='secondary'>Nothing outstanding — all late closers are settled.</Text>
      ) : (
        <>
          <Text type='secondary' className='block mb-2'>
            {rateTopup
              ? 'Top-up: rate-specified lines (% of committed / balance) bill late closers at the original call rates — no credits, new money. Fixed-amount lines (expenses, fixed draws) always re-pool with credits.'
              : 'Reallocate: every line re-pools as-if-from-day-one over the current pool — late closers buy in, early closers receive credits carried forward against their next call.'}
          </Text>
          <Table
            size='small'
            pagination={false}
            rowKey='fund_commitments__id'
            dataSource={eqRows}
            columns={equalizationColumns}
            summary={() => (
              <Table.Summary fixed>
                <Table.Summary.Row className='font-medium'>
                  <Table.Summary.Cell index={0}>Total outstanding</Table.Summary.Cell>
                  <Table.Summary.Cell index={1} />
                  <Table.Summary.Cell index={2} />
                  <Table.Summary.Cell index={3} align='right'>
                    {(eqTotals?.fee_catchup_total ?? 0).toLocaleString('en-US', {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2
                    })}
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={4} align='right'>
                    <Text type='secondary'>
                      {(eqTotals?.fixed_realloc_charge_total ?? 0).toLocaleString('en-US', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2
                      })}{' '}
                      / 0.00 net
                    </Text>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={5} align='right'>
                    {rateTopup ? (
                      (eqTotals?.rate_topup_charge_total ?? 0).toLocaleString('en-US', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2
                      })
                    ) : (
                      <Text type='secondary'>
                        {(eqTotals?.rate_realloc_charge_total ?? 0).toLocaleString('en-US', {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2
                        })}{' '}
                        / 0.00 net
                      </Text>
                    )}
                  </Table.Summary.Cell>
                </Table.Summary.Row>
              </Table.Summary>
            )}
          />
          {equalization?.fee_catchup_status_message || equalization?.equalization_status_message ? (
            <Text type='secondary' className='block mt-2'>
              {[equalization?.fee_catchup_status_message, equalization?.equalization_status_message]
                .filter(Boolean)
                .join(' ')}
            </Text>
          ) : null}
        </>
      )}
    </FormSection>
  )
}

export default EqualizationSection
